import { CircularProgress } from '@mui/material';

import { cn } from '@/shared/lib/cn';

/**
 * How much room the loader claims.
 *
 * `inline` sits inside a line of content; `section` reserves enough height that
 * a card body does not collapse and then jump when the data lands; `page` fills
 * the viewport for a route that has nothing else to show yet.
 */
export type LoaderSize = 'inline' | 'section' | 'page';

const SIZES: Record<LoaderSize, { spinner: number; wrapper: string }> = {
  inline: { spinner: 20, wrapper: 'py-1' },
  section: { spinner: 32, wrapper: 'min-h-32 py-6' },
  page: { spinner: 48, wrapper: 'min-h-dvh' },
};

interface LoaderProps {
  size?: LoaderSize;
  /** Announced to assistive tech and never shown. Say what is loading — "Loading"
   *  alone tells a screen-reader user nothing about which region is waiting. */
  label: string;
  className?: string;
}

/**
 * The one loading indicator in this app.
 *
 * Every wait looks the same on purpose: skeletons promise a shape the data may
 * not match, and mixing them with spinners makes two waits of the same kind
 * look like two different events. A spinner makes no claim about what arrives.
 *
 * The label goes on `aria-label`, not in a child — `role="status"` takes no
 * accessible name from its content, so an `sr-only` span leaves the live region
 * unnamed.
 */
export const Loader = ({ size = 'section', label, className }: LoaderProps) => {
  const { spinner, wrapper } = SIZES[size];

  return (
    <div
      role="status"
      aria-label={label}
      className={cn('flex w-full items-center justify-center', wrapper, className)}
    >
      <CircularProgress size={spinner} />
    </div>
  );
};
